import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from "recharts";
import {
  Users, FileCheck2, AlertTriangle, CalendarDays, ClipboardList, Banknote,
  Activity, ChevronRight, UserCircle, CheckCircle2, Clock, LayoutGrid,
  Truck, FileText, ShieldCheck, ArrowLeft, HardHat,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, KPI, Pill, Button } from "@/components/ui";
import { format, parseISO, formatDistanceToNow, differenceInDays, startOfWeek, addDays } from "date-fns";

// =============================================================================
// T-497 — HR widgets.
//
// Everything here is built around one question the old screen could not answer:
// can this person go to site tomorrow. An expired licence is not an admin
// nuisance, it is a person who legally should not be on a tower, and for ISO it
// is a finding. So compliance is counted PER PERSON, not per licence — "8
// overdue" tells you nothing about whether that is one bloke or eight.
// =============================================================================

const EXPIRING_WINDOW_DAYS = 60;

function TeamComplianceCard() {
  const { data, isLoading } = useQuery({
    queryKey: ["hr_hub_team_compliance"],
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const soon  = new Date(Date.now() + EXPIRING_WINDOW_DAYS * 86400000).toISOString().slice(0, 10);

      const [{ data: emps }, { data: lics }] = await Promise.all([
        supabase.from("employees").select("employee_id, first_name, last_name").eq("is_active", true),
        supabase.from("employee_licences")
          .select("licence_id, employee_id, expiry_date, licence_type:licence_type_id(display_name, code)")
          .eq("is_active", true),
      ]);

      const people = new Map<string, { name: string; overdue: any[]; expiring: any[]; total: number }>();
      for (const e of (emps ?? []) as any[]) {
        people.set(e.employee_id, {
          name: [e.first_name, e.last_name].filter(Boolean).join(" ") || "—",
          overdue: [], expiring: [], total: 0,
        });
      }
      for (const l of (lics ?? []) as any[]) {
        const p = people.get(l.employee_id);
        if (!p) continue;                       // licence on an inactive employee
        p.total += 1;
        if (!l.expiry_date) continue;           // no expiry = does not lapse
        if (l.expiry_date < today)      p.overdue.push(l);
        else if (l.expiry_date <= soon) p.expiring.push(l);
      }

      const list = Array.from(people.values());
      const blocked  = list.filter(p => p.overdue.length > 0);
      const watch    = list.filter(p => p.overdue.length === 0 && p.expiring.length > 0);
      const clear    = list.filter(p => p.overdue.length === 0 && p.expiring.length === 0);

      return {
        headcount: list.length,
        blocked: blocked.sort((a, b) => b.overdue.length - a.overdue.length),
        watchCount: watch.length,
        clearCount: clear.length,
        donut: [
          { name: "Overdue",  value: blocked.length, colour: "#EF4444" },
          { name: "Expiring", value: watch.length,   colour: "#FBBF24" },
          { name: "Current",  value: clear.length,   colour: "#10B981" },
        ].filter(d => d.value > 0),
      };
    },
  });

  return (
    <Card className="lg:col-span-1">
      <div className="p-4">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <ShieldCheck size={14} className="text-accent"/>
            <h3 className="text-[13px] font-semibold text-text">Team compliance</h3>
          </div>
          <Link to="/hr/licences" className="text-[11px] text-accent hover:underline">Licences →</Link>
        </div>
        <p className="text-[10px] text-text-dim">by person, not by licence</p>

        {isLoading ? (
          <div className="py-14 text-center text-[12px] text-text-muted italic">Loading…</div>
        ) : !data || data.headcount === 0 ? (
          <div className="py-14 text-center text-[12px] text-text-muted italic">No active employees.</div>
        ) : (
          <>
            <div className="relative h-[112px] mt-2">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.donut} dataKey="value" nameKey="name"
                    cx="50%" cy="50%" innerRadius={34} outerRadius={50} paddingAngle={2} stroke="none">
                    {data.donut.map((d, i) => <Cell key={i} fill={d.colour}/>)}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#14161a", border: "1px solid #2a2e35", borderRadius: 6, fontSize: 12, padding: "6px 10px" }}
                    itemStyle={{ color: "#f5f5f5", fontSize: 12 }}
                    labelStyle={{ color: "#9aa0a6", fontSize: 11, marginBottom: 2 }}
                    formatter={(v: any, n: any) => [`${v} ${Number(v) === 1 ? "person" : "people"}`, n]}/>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-2xl font-semibold text-text leading-none">{data.headcount}</span>
                <span className="text-[10px] text-text-dim mt-0.5">on the books</span>
              </div>
            </div>

            <div className="mt-2 flex items-center justify-center gap-3 text-[11px]">
              {data.donut.map(d => (
                <span key={d.name} className="inline-flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{ background: d.colour }}/>
                  <span className="text-text-muted">{d.name}</span>
                  <span className="font-mono text-text">{d.value}</span>
                </span>
              ))}
            </div>

            {data.blocked.length === 0 ? (
              <div className="mt-3 text-center text-[12px] text-ok italic">
                Nobody is carrying an expired licence.
              </div>
            ) : (
              <div className="mt-2">
                <div className="text-[11px] uppercase tracking-wider2 text-danger mb-2">
                  Expired — check before rostering
                </div>
                <div className="space-y-2">
                  {data.blocked.slice(0, 5).map(p => (
                    <Link key={p.name} to="/hr/licences" className="block group">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-danger shrink-0"/>
                        <span className="text-[13px] text-text truncate group-hover:text-accent transition-colors">
                          {p.name}
                        </span>
                        <span className="ml-auto text-[13px] font-mono text-danger shrink-0">{p.overdue.length}</span>
                      </div>
                      <div className="text-[11px] text-text-dim truncate pl-3.5">
                        {p.overdue.slice(0, 2).map((l: any) => l.licence_type?.code ?? "—").join(", ")}
                        {p.overdue.length > 2 ? ` +${p.overdue.length - 2}` : ""}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Coverage by licence type. The crewing question rather than the compliance one:
// if only one person holds a current EWP ticket, that job stops when he takes
// leave. Thin coverage is a risk you cannot see from a per-person view.
function LicenceCoverageCard() {
  const { data, isLoading } = useQuery({
    queryKey: ["hr_hub_licence_coverage"],
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const [{ data: emps }, { data: lics }] = await Promise.all([
        supabase.from("employees").select("employee_id").eq("is_active", true),
        supabase.from("employee_licences")
          .select("employee_id, expiry_date, licence_type:licence_type_id(display_name, code, category)")
          .eq("is_active", true),
      ]);

      const active = new Set((emps ?? []).map((e: any) => e.employee_id));
      const byType = new Map<string, { code: string; name: string; current: Set<string>; lapsed: Set<string> }>();

      for (const l of (lics ?? []) as any[]) {
        if (!active.has(l.employee_id)) continue;
        const code = l.licence_type?.code ?? "—";
        const e = byType.get(code) ?? {
          code,
          name: l.licence_type?.display_name ?? code,
          current: new Set<string>(), lapsed: new Set<string>(),
        };
        // No expiry means it does not lapse, so it counts as current.
        if (!l.expiry_date || l.expiry_date >= today) e.current.add(l.employee_id);
        else e.lapsed.add(l.employee_id);
        byType.set(code, e);
      }

      const rows = Array.from(byType.values())
        .map(t => ({
          code: t.code, name: t.name,
          current: t.current.size,
          // Someone whose only copy of this ticket has expired is a gap, not a holder.
          lapsedOnly: Array.from(t.lapsed).filter(id => !t.current.has(id)).length,
        }))
        .sort((a, b) => (b.lapsedOnly - a.lapsedOnly) || (a.current - b.current) || a.code.localeCompare(b.code));

      return {
        headcount: active.size,
        rows: rows.slice(0, 8),
        typeCount: byType.size,
        // The number that actually matters: tickets only one person can cover.
        singlePoint: rows.filter(r => r.current <= 1).length,
      };
    },
  });

  return (
    <Card className="lg:col-span-1">
      <div className="p-4">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <FileCheck2 size={14} className="text-accent"/>
            <h3 className="text-[13px] font-semibold text-text">Licence coverage</h3>
          </div>
          <Link to="/hr/licences" className="text-[11px] text-accent hover:underline">Register →</Link>
        </div>
        <p className="text-[10px] text-text-dim">who you could send on a job needing that ticket</p>

        {isLoading ? (
          <div className="py-14 text-center text-[12px] text-text-muted italic">Loading…</div>
        ) : !data || data.rows.length === 0 ? (
          <div className="py-14 text-center text-[12px] text-text-muted italic">No licences recorded.</div>
        ) : (
          <>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-semibold text-text tracking-tight">{data.singlePoint}</span>
              <span className="text-[12px] text-text-muted">
                {data.singlePoint === 1 ? "ticket rests on one person" : "tickets rest on one person"}
              </span>
            </div>
            <p className="mt-1 text-[11px] text-text-muted">
              If that person is away or lapses, work needing it stops.
            </p>

            <div className="mt-3 space-y-2.5">
              {data.rows.map(t => {
                const pct = data.headcount > 0 ? (t.current / data.headcount) * 100 : 0;
                const thin = t.current <= 1;
                return (
                  <div key={t.code}>
                    <div className="flex items-baseline gap-2">
                      <span className="text-[12px] font-mono text-text truncate max-w-[50%]" title={t.name}>
                        {t.code}
                      </span>
                      <span className="ml-auto text-[12px] shrink-0">
                        <span className={thin ? "text-warn font-semibold" : "text-text"}>{t.current}</span>
                        <span className="text-text-dim"> of {data.headcount} ready</span>
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="h-1.5 flex-1 rounded-full bg-ink overflow-hidden">
                        <div className="h-full rounded-full"
                          style={{ width: `${Math.max(3, pct)}%`,
                                   background: thin ? "#FBBF24" : "#10B981" }}/>
                      </div>
                      {t.lapsedOnly > 0 && (
                        <span className="text-[10px] text-danger shrink-0 font-mono"
                          title="people who hold this ticket but theirs has expired">
                          {t.lapsedOnly} expired
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-3 text-[10px] text-text-dim">
              "Ready" = holds a current copy. "Expired" = has it, but it has lapsed —
              renew and they are back. Riskiest first.
            </p>
          </>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Team week. Leave barely gets used at Coretel, so an availability panel was an
// empty box; hours are entered every week, so this is the live one.
//
// The filter matters more than it looks: not everyone books time the same way,
// and a fourteen-row grid where half the rows are blank reads as a problem when
// it isn't. Pick the people you actually supervise and the rest drop out.
function TeamWeekCard() {
  const [picked, setPicked] = useState<string[] | null>(null);   // null = auto
  const [openPicker, setOpenPicker] = useState(false);

  const weekStart = useMemo(
    () => format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd"), []);
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => format(addDays(parseISO(weekStart), i), "yyyy-MM-dd")), [weekStart]);

  const { data, isLoading } = useQuery({
    queryKey: ["hr_hub_team_week", weekStart],
    queryFn: async () => {
      const weekEnd = days[6];
      const [{ data: emps }, { data: entries }] = await Promise.all([
        supabase.from("employees").select("employee_id, first_name, last_name").eq("is_active", true).order("first_name"),
        supabase.from("timesheet_entries")
          .select("employee_id, work_date, total_hours, submitted_at, approved_at")
          .gte("work_date", weekStart).lte("work_date", weekEnd),
      ]);

      const byEmp = new Map<string, { hours: Map<string, number>; total: number; submitted: boolean; anySubmitted: boolean }>();
      for (const e of (entries ?? []) as any[]) {
        const row = byEmp.get(e.employee_id) ?? { hours: new Map(), total: 0, submitted: true, anySubmitted: false };
        row.hours.set(e.work_date, (row.hours.get(e.work_date) ?? 0) + Number(e.total_hours ?? 0));
        row.total += Number(e.total_hours ?? 0);
        if (e.submitted_at) row.anySubmitted = true; else row.submitted = false;
        byEmp.set(e.employee_id, row);
      }

      const people = ((emps ?? []) as any[]).map(e => {
        const r = byEmp.get(e.employee_id);
        return {
          employee_id: e.employee_id,
          name: [e.first_name, e.last_name].filter(Boolean).join(" ") || "—",
          hours: r?.hours ?? new Map<string, number>(),
          total: r?.total ?? 0,
          submitted: !!r && r.submitted && r.anySubmitted,
          hasEntries: !!r,
        };
      });

      const peak = Math.max(0, ...people.flatMap(p => Array.from(p.hours.values())));
      return { people, peak, weekTotal: people.reduce((s, p) => s + p.total, 0) };
    },
  });

  const all = data?.people ?? [];
  // Default view: people who have actually booked something this week.
  const visible = picked === null ? all.filter(p => p.hasEntries) : all.filter(p => picked.includes(p.employee_id));

  const toggle = (id: string) => {
    const base = picked ?? all.filter(p => p.hasEntries).map(p => p.employee_id);
    setPicked(base.includes(id) ? base.filter(x => x !== id) : [...base, id]);
  };

  const cell = (h: number) => {
    if (h <= 0) return { background: "rgb(var(--color-ink))", border: "1px solid rgb(var(--color-line))" };
    const t = data?.peak ? Math.min(1, h / data.peak) : 0.5;
    return { background: `rgba(249, 115, 22, ${0.2 + t * 0.8})`, border: "1px solid transparent" };
  };

  return (
    <Card className="lg:col-span-2">
      <div className="p-4">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-accent"/>
            <h3 className="text-[13px] font-semibold text-text">Team week</h3>
            <span className="text-[10px] font-mono text-text-dim hidden sm:inline">
              · w/c {format(parseISO(weekStart), "d MMM")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setOpenPicker(v => !v)}
              className="text-[11px] text-accent hover:underline">
              {picked === null ? "Filter people" : `${visible.length} shown`}
            </button>
            <Link to="/hr/team-week" className="text-[11px] text-accent hover:underline">Team view →</Link>
          </div>
        </div>
        <p className="text-[10px] text-text-dim">hours booked this week</p>

        {openPicker && (
          <div className="mt-2 p-2 rounded border border-line bg-ink">
            <div className="flex items-center gap-1.5 flex-wrap">
              {all.map(p => {
                const on = visible.some(v => v.employee_id === p.employee_id);
                return (
                  <button key={p.employee_id} type="button" onClick={() => toggle(p.employee_id)}
                    className={`px-2 py-0.5 rounded-full text-[10px] border transition-colors ${
                      on ? "bg-accent text-white border-accent" : "bg-ink text-text-muted border-line hover:border-accent/50"}`}>
                    {p.name.split(" ")[0]}
                  </button>
                );
              })}
            </div>
            <button type="button" onClick={() => { setPicked(null); setOpenPicker(false); }}
              className="mt-2 text-[10px] text-text-dim hover:text-accent">
              reset to everyone with hours
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="py-14 text-center text-[12px] text-text-muted italic">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="py-14 text-center text-[12px] text-text-muted italic">
            {all.length === 0 ? "No active employees." : "Nobody has booked hours this week yet."}
          </div>
        ) : (
          <>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-2xl font-semibold text-text tracking-tight">
                {Math.round(data?.weekTotal ?? 0).toLocaleString()}
              </span>
              <span className="text-[12px] text-text-muted">hours booked</span>
              <span className="text-[11px] text-text-dim ml-auto">
                {visible.filter(p => p.submitted).length} of {visible.length} submitted
              </span>
            </div>

            <div className="mt-3">
              <div className="flex items-center gap-2 mb-1 pl-[92px]">
                {days.map(d => (
                  <div key={d} className="flex-1 text-center text-[9px] text-text-dim uppercase">
                    {format(parseISO(d), "EEEEE")}
                  </div>
                ))}
                <div className="w-12 text-right text-[9px] text-text-dim uppercase">tot</div>
              </div>
              <div className="space-y-1">
                {visible.map(p => (
                  <div key={p.employee_id} className="flex items-center gap-2">
                    <div className="w-[88px] shrink-0 text-[11px] text-text truncate" title={p.name}>
                      {p.name}
                    </div>
                    {days.map(d => {
                      const h = p.hours.get(d) ?? 0;
                      return (
                        <div key={d} className="flex-1 h-5 rounded-[3px] flex items-center justify-center"
                          title={`${p.name} — ${format(parseISO(d), "EEE d MMM")}: ${h ? h.toFixed(1) + "h" : "nothing booked"}`}
                          style={cell(h)}>
                          {h > 0 && <span className="text-[9px] font-mono text-black/70">{Math.round(h)}</span>}
                        </div>
                      );
                    })}
                    <div className={`w-12 shrink-0 text-right text-[11px] font-mono ${
                      p.submitted ? "text-ok" : p.total > 0 ? "text-warn" : "text-text-dim"}`}>
                      {p.total > 0 ? `${Math.round(p.total)}h` : "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p className="mt-3 text-[10px] text-text-dim">
              Green total = submitted. Amber = logged but not submitted yet.
            </p>
          </>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
function NavTile({ to, label, icon: Icon }: { to: string; label: string; icon: any }) {
  return (
    <Link
      to={to}
      className="group inline-flex items-center gap-2 px-3 py-2.5 rounded bg-ink-raised border border-line text-accent hover:border-accent hover:bg-ink-hover transition-colors"
    >
      <Icon size={14} className="flex-shrink-0" />
      <span className="text-[12px] font-medium truncate">{label}</span>
    </Link>
  );
}

// =============================================================================
// HrHubScreen — /hr (T-075 Phase C — HR Centre dashboard)
//
// Director / manager-only people-focused dashboard. Mirrors Project + Finance
// Command Centre treatment for consistency across hubs.
// =============================================================================

const LEAVE_STATUS_TONE: Record<string, "open" | "pending" | "approved" | "rejected" | "default"> = {
  submitted:  "pending",
  approved:   "approved",
  rejected:   "rejected",
  cancelled:  "default",
};

// -----------------------------------------------------------------------------

export default function HrHubScreen() {
  const user = useRequireAuth();
  const canManage = hasRole(user, "manager");

  // -- KPIs --------------------------------------------------------------
  const { data: employeeStats } = useQuery({
    queryKey: ["hr_hub_employees"],
    queryFn: async () => {
      const { count } = await supabase
        .from("employees")
        .select("employee_id", { count: "exact", head: true });
      return { count: count ?? 0 };
    },
  });

  const { data: licenceStats } = useQuery({
    queryKey: ["hr_hub_licences"],
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const in60  = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

      const [overdueRes, soonRes, totalRes] = await Promise.all([
        supabase.from("employee_licences").select("licence_id", { count: "exact", head: true })
          .eq("is_active", true).lt("expiry_date", today),
        supabase.from("employee_licences").select("licence_id", { count: "exact", head: true })
          .eq("is_active", true).gte("expiry_date", today).lte("expiry_date", in60),
        supabase.from("employee_licences").select("licence_id", { count: "exact", head: true })
          .eq("is_active", true),
      ]);

      return {
        overdue: overdueRes.count ?? 0,
        soon:    soonRes.count ?? 0,
        total:   totalRes.count ?? 0,
      };
    },
  });

  const { data: leaveStats } = useQuery({
    queryKey: ["hr_hub_leave"],
    queryFn: async () => {
      const { data: pending } = await supabase
        .from("leave_requests")
        .select("request_id, request_number, employee_id, leave_type_code, starts_on, ends_on, days_requested, status, submitted_at, employee:employee_id(first_name, last_name)")
        .eq("status", "submitted")
        .order("submitted_at", { ascending: true })
        .limit(5);
      return {
        pending: (pending ?? []) as Array<any>,
        pendingCount: pending?.length ?? 0,
      };
    },
  });

  // -- T-286: Team in progress this week ----------------------------------
  // After T-276 made /timesheets personal-only, directors lost visibility into
  // entries logged but not yet submitted. This panel restores that — shows
  // anyone in the team with timesheet_entries this week where submitted_at
  // is NULL, grouped by employee with hours-so-far + day count.
  const { data: inProgress = [] } = useQuery({
    queryKey: ["hr_hub_timesheets_in_progress"],
    enabled: canManage,
    queryFn: async () => {
      // Monday-of-this-week (matches the TimesheetsScreen weekStart logic)
      const today = new Date();
      const day   = today.getDay();                          // 0=Sun, 6=Sat
      const diff  = day === 0 ? -6 : 1 - day;                // back to Monday
      const monday = new Date(today);
      monday.setDate(today.getDate() + diff);
      const mondayIso = monday.toISOString().slice(0, 10);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      const sundayIso = sunday.toISOString().slice(0, 10);

      const { data, error } = await supabase
        .from("timesheet_entries")
        .select(`
          entry_id, employee_id, work_date, total_hours, submitted_at,
          employee:employee_id(first_name, last_name)
        `)
        .gte("work_date", mondayIso)
        .lte("work_date", sundayIso)
        .is("submitted_at", null);
      if (error) throw error;

      // Group by employee
      const byEmp = new Map<string, {
        employee_id: string;
        name: string;
        hours: number;
        days: Set<string>;
      }>();
      for (const r of (data ?? []) as any[]) {
        const id = r.employee_id;
        const name = [r.employee?.first_name, r.employee?.last_name].filter(Boolean).join(" ") || "Unnamed";
        const g = byEmp.get(id) ?? { employee_id: id, name, hours: 0, days: new Set<string>() };
        g.hours += Number(r.total_hours) || 0;
        g.days.add(r.work_date);
        byEmp.set(id, g);
      }
      return Array.from(byEmp.values())
        .map(g => ({ employee_id: g.employee_id, name: g.name, hours: g.hours, days: g.days.size }))
        .sort((a, b) => b.hours - a.hours);
    },
  });

  // -- Soonest expiring licences (top 5) ---------------------------------
  const { data: nextExpiring = [] } = useQuery({
    queryKey: ["hr_hub_next_expiring"],
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const { data } = await supabase
        .from("employee_licences")
        .select(`licence_id, expiry_date, licence_number,
                 employee:employee_id(first_name, last_name),
                 licence_type:licence_type_id(display_name, code)`)
        .eq("is_active", true)
        .gte("expiry_date", today)
        .order("expiry_date", { ascending: true })
        .limit(6);
      return data ?? [];
    },
  });

  if (!user) return null;

  // Container — full viewport width, see ProjectHub for rationale.
  return (
    <div className="px-6 md:px-10 py-6 md:py-8 pb-20">
      <Link to="/" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14}/> Home
      </Link>
      {/* Header */}
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <div className="h-1 w-10 bg-accent rounded-full" />
            <div className="text-text-muted text-[11px] tracking-wider2 uppercase font-mono">HR portal</div>
          </div>
          <h1 className="text-[24px] md:text-[28px] font-semibold tracking-tight flex items-center gap-2">
            <Users size={24} className="text-accent" />
            People Centre
          </h1>
          <p className="text-text-muted text-[13px] mt-1">
            Employees, licences, leave, inductions — the people side of the operation.
          </p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2 flex-wrap">
            {/* James, 22 Aug 2026: "remove this one, keep [Licence oversight] as
                a quick nav to licences." Leave approvals was in the header AND
                the Jump To grid below - the tile stays, the chip goes. Same
                pattern as T-305 removing the Timesheet approvals chip. */}
            <Link to="/hr/licences">
              <Button variant="secondary" size="sm" icon={FileCheck2}>Licence oversight</Button>
            </Link>
            {/* T-305: removed "Timesheet approvals" header button. Same
                destination reachable via the "Timesheets" tile in the Jump To
                grid below + the "Approvals queue" button on the in-progress
                panel. Reduces header clutter without losing the route. */}
            <Link to="/people">
              <Button variant="primary" size="sm" icon={Users}>Employees</Button>
            </Link>
          </div>
        )}
      </div>

      {/* HR portal nav — quick-jump tiles */}
      <div className="mb-6">
        <div className="text-text-muted text-[10px] tracking-wider2 uppercase font-mono mb-2">
          Jump to
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
          <NavTile to="/" label="All hubs" icon={LayoutGrid} />
          <NavTile to="/people" label="Employees" icon={Users} />
          <NavTile to="/hr/leave-approvals" label="Leave" icon={CalendarDays} />
          <NavTile to="/hr/timesheet-approvals" label="Timesheets" icon={Clock} />
          {/* T-311 — read-only team week grid for manager+ visibility */}
          <NavTile to="/hr/team-week" label="Team week" icon={Users} />
          <NavTile to="/hr/compliance" label="Compliance" icon={FileCheck2} />
          <NavTile to="/hr/inductions" label="Inductions" icon={ShieldCheck} />
          <NavTile to="/hr/equipment" label="Equipment" icon={HardHat} />
          <NavTile to="/assets" label="Assets" icon={Truck} />
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <KPI label="Employees"        value={employeeStats?.count ?? "—"} icon={Users}       sub="active" />
        <KPI label="Licences total"   value={licenceStats?.total ?? "—"}  icon={FileCheck2}  sub="across team" />
        <KPI label="Expiring soon"    value={licenceStats?.soon ?? "—"}   icon={Clock}        sub="within 60d" accent={(licenceStats?.soon ?? 0) > 0} />
        <KPI label="Licences overdue" value={licenceStats?.overdue ?? "—"} icon={AlertTriangle} sub="action needed" accent={(licenceStats?.overdue ?? 0) > 0} />
        <KPI label="Leave pending"    value={leaveStats?.pendingCount ?? "—"} icon={CalendarDays} sub="awaiting approval" accent={(leaveStats?.pendingCount ?? 0) > 0} />
        <KPI label="Inductions"       value="—"                            icon={ClipboardList} sub="T-005 coming" />
      </div>

      {/* T-286 — Team timesheets in progress this week. After T-276 made
          /timesheets personal-only, this panel restores manager visibility
          into who's logged hours but hasn't pressed "Submit my week" yet.
          Empty state means everyone's either submitted or hasn't logged
          anything yet — both are fine. */}
      {canManage && inProgress.length > 0 && (
        <Card className="mb-6">
          <div className="p-4">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Clock size={14} className="text-warn"/>
                <h3 className="text-[13px] font-semibold text-text">Timesheets in progress this week</h3>
                <span className="text-[10px] font-mono text-text-dim hidden sm:inline">
                  · {inProgress.length} {inProgress.length === 1 ? "person" : "people"} haven't pressed Submit yet
                </span>
              </div>
              <div className="flex items-center gap-2">
                {/* T-311 — drill into the full team week grid to see what's
                    being booked against which project before submission. */}
                <Link to="/hr/team-week">
                  <Button variant="secondary" size="sm">Team week view</Button>
                </Link>
                <Link to="/hr/timesheet-approvals">
                  <Button variant="secondary" size="sm" icon={ChevronRight}>Approvals queue</Button>
                </Link>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono border-b border-line">
                    <th className="text-left  py-2 pr-3">Employee</th>
                    <th className="text-right py-2 px-3">Days logged</th>
                    <th className="text-right py-2 px-3">Hours so far</th>
                    <th className="text-left  py-2 pl-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {inProgress.slice(0, 10).map(r => (
                    <tr key={r.employee_id} className="border-b border-line/40 hover:bg-ink-hover">
                      <td className="py-2 pr-3 text-text">{r.name}</td>
                      <td className="py-2 px-3 text-right font-mono">{r.days}/7</td>
                      <td className="py-2 px-3 text-right font-mono">{r.hours.toFixed(1)}h</td>
                      <td className="py-2 pl-3">
                        <Pill label="not submitted" tone="pending" small />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {inProgress.length > 10 && (
              <div className="text-[11px] text-text-muted mt-2 font-mono">
                · {inProgress.length - 10} more — go to Approvals queue for full list
              </div>
            )}
            <div className="text-[11px] text-text-muted mt-3">
              These entries have been logged but the person hasn't pressed <span className="font-mono text-text">Submit my week</span> yet, so they won't appear in the Approvals queue until they do. Chase up if Friday's getting close.
            </div>
          </div>
        </Card>
      )}

      {/* T-497 — the licence pipeline was three bars saying "8 / 3 / 0" and the
          leave queue was usually empty. Both replaced with the questions this
          portal actually has to answer: who cannot go to site, where the team's
          licence coverage is thin, and who is away. */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        <TeamComplianceCard />
        <LicenceCoverageCard />
        <TeamWeekCard />
      </div>

      {/* Bottom: soonest-expiring licences list */}
      <Card>
        <div className="p-4">
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-2">
              <Activity size={14} className="text-accent"/>
              <h3 className="text-[13px] font-semibold text-text">Next licences expiring</h3>
              <span className="text-[10px] font-mono text-text-dim hidden sm:inline">· soonest first</span>
            </div>
            <Link to="/hr/licences" className="text-[11px] text-accent hover:underline">Full register →</Link>
          </div>
          {nextExpiring.length === 0 ? (
            <div className="py-8 text-center text-[12px] text-text-muted italic">
              No upcoming licence expiries.
            </div>
          ) : (
            <div className="mt-3 space-y-1.5">
              {nextExpiring.map((lic: any) => {
                const daysOut = differenceInDays(parseISO(lic.expiry_date), new Date());
                const tone = daysOut <= 30 ? "warning" : daysOut <= 60 ? "default" : "default";
                return (
                  <div key={lic.licence_id} className="p-2.5 rounded bg-ink border border-line flex items-center gap-3">
                    <UserCircle size={14} className="text-text-muted flex-shrink-0"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] text-text truncate">
                        {lic.employee?.first_name} {lic.employee?.last_name} · <span className="font-mono text-text-muted">{lic.licence_type?.code ?? "—"}</span>
                      </div>
                      <div className="text-[10px] text-text-dim mt-0.5">
                        {lic.licence_type?.display_name ?? "Licence"}
                        {lic.licence_number && <> · #{lic.licence_number}</>}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`text-[12px] font-medium ${daysOut <= 30 ? "text-warn" : "text-text"}`}>
                        {daysOut}d
                      </div>
                      <div className="text-[10px] font-mono text-text-dim">
                        {format(parseISO(lic.expiry_date), "dd MMM")}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
